import { forwardRef, useMemo, useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import editorStyles from './editor-styles';
import AppLoader from '../../../../components/app-loader';

// --- File picker handler ---
const filePicker = (callback, meta) => {
    // Create a hidden input element for file selection
    const input = document.createElement('input');
    input.type = 'file';
    // Allow only images, or video/audio depending on meta.filetype
    input.accept = meta.filetype === 'media' ? 'video/*,audio/*' : 'image/*';

    // Handle file selection
    input.onchange = () => {
        const file = input.files[0];
        const reader = new FileReader();
        // When the file is loaded, return it via callback
        reader.onload = (e) => {
            callback(e.target.result, { alt: file.name });
        };
        // Read the file as a Base64 Data URL
        reader.readAsDataURL(file);
    };
    // Open the file picker dialog
    input.click();
};

// --- Keydown handler ---
const setupEditor = (editor) => {
    // Attach a keydown listener on the editor
    editor.on('keydown', (event) => {
        // Only act on Backspace/Delete keys
        if (event.key === 'Backspace' || event.key === 'Delete') {
            const selection = editor.selection;
            const node = selection.getNode();

            // Check where the cursor is placed
            const isInsideFormGroup = node.closest('.form-group');
            const isFormContainer = node.closest('.mceNonEditable');
            const isTableCell = node.closest('td');

            // Prevent deleting .mceNonEditable container if not inside a table or form-group
            if (!isTableCell && !isInsideFormGroup && isFormContainer) {
                event.preventDefault();
                return;
            }

            // Additional restriction for Backspace
            if (event.key === 'Backspace') {
                const rng = selection.getRng();
                // If cursor is collapsed (no text selected, just blinking caret)
                if (rng.collapsed) {
                    const prevNode = rng.startContainer.previousSibling;
                    // If previous sibling is a non-editable block, block deletion
                    if (prevNode && prevNode.nodeType === 1 && prevNode.classList.contains('mceNonEditable')) {
                        event.preventDefault();
                    }
                }
            }
        }
    });
};

const ReportEditor = forwardRef(({ value, onChange, mode, editorRef }) => {
    const [loading, setLoading] = useState(true);

    // Memoize config so it’s not rebuilt on every render
    const editorConfig = useMemo(
        () => ({
            selector: 'textarea',
            height: 700,
            readonly: true,
            visual: false,
            extended_valid_elements: '*[*]',
            plugins: ['table', 'image', 'media', 'link', 'code', 'fullscreen', 'lists'],
            toolbar:
                'template | undo redo | formatselect | template fullscreen | ' +
                'image media link | ' +
                'bold italic backcolor | alignleft aligncenter ' +
                'alignright alignjustify | bullist numlist outdent indent | ' +
                'removeformat | help ',
            setup: (editor) => {
                editor.on('init', () => {
                    // Just to be extra safe in case onInit doesn't catch everything
                    setLoading(false);
                });
            },
            image_title: true,
            menu: { file: { title: 'File', items: 'newdocument' } },
            file_picker_types: 'image media',
            setup: setupEditor,
            file_picker_callback: filePicker,
            content_style: editorStyles,
        }),
        []
    );

    return (
        <div style={{ position: 'relative', minHeight: '200px' }}>
            {/* Loader Component  */}
            <AppLoader visible={loading} />

            <Editor
                tinymceScriptSrc={process.env.PUBLIC_URL + '/tinymce/tinymce.min.js'}
                ref={editorRef}
                value={value}
                onEditorChange={onChange}
                disabled={mode === 'view'}
                init={editorConfig}
                // Hide loader when ready
                onInit={() => setLoading(false)}
            />
        </div>
    );
});

export default ReportEditor;
